import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { api, unwrap } from "@/client/lib/api";
import {
  ensureTenantData,
  onboardingPath,
  requireOnboarding,
  sessionQuery,
} from "@/client/lib/session";
import type { OnboardingState } from "@/features/onboarding/schemas";

export const onboardingQuery = queryOptions({
  queryKey: ["onboarding"],
  queryFn: (): Promise<OnboardingState> => unwrap(api.onboarding.$get()),
});

/**
 * The address follows the step the server holds, never the other way round, so a refresh or
 * another device opens the step the person stopped at. Returns the company the step was read for,
 * which the layout keys its screens by.
 */
export async function resumeOnboarding(
  queryClient: QueryClient,
  pathname: string,
  href: string,
  preload = false,
): Promise<{ onboardingCompany: string }> {
  await requireOnboarding(queryClient, href, preload);

  const state = await ensureTenantData(queryClient, onboardingQuery);

  const path = onboardingPath(state.step);
  if (!path) {
    // The session snapshot still holds the step it was read with, and the company's guard would
    // send the person straight back here.
    queryClient.removeQueries({ queryKey: sessionQuery.queryKey });
    // No trial at launch: an Admin of a company that has never paid lands where it pays.
    const { role, companyPlan } =
      await queryClient.ensureQueryData(sessionQuery);
    const unpaid =
      role === "admin" &&
      companyPlan?.state === "expired" &&
      !companyPlan.subscribed;
    throw redirect({
      to: "/$slug",
      params: { slug: state.companySlug },
      search: unpaid ? { settings: "billing" } : undefined,
    });
  }
  if (pathname !== path) throw redirect({ to: path });
  return { onboardingCompany: state.companySlug };
}

/**
 * Whoever created the company also named it, between the profile and the invite step, so their
 * count includes that step: "Step 3 of 3" on the invite step. Everyone else counts the server's.
 */
export function shownPosition(state: OnboardingState): {
  position: number;
  total: number;
} {
  if (!state.steps.includes("invite_team")) {
    return { position: state.position, total: state.steps.length };
  }
  return { position: state.step === "invite_team" ? 3 : 1, total: 3 };
}
